import React, { useEffect, useState, JSX } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
} from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import { AdminSessionModal } from 'components/admin/AdminSessionModal';
import * as Types from 'types/types';

type UserSummary = {
  id?: string;
  email?: string;
  username?: string;
  lastLogin?: string | Date;
};

type AdminOverview = {
  totalUsers?: number;
  newUsersLastDay?: number;
  activeUsersLastDay?: number;
  recentUserAccess?: UserSummary[];
  recentChats?: Types.ChatSession[];
};

function formatDate(value?: string | Date): string {
  if (!value) return '';
  if (value instanceof Date) return value.toLocaleString();
  const d = new Date(value as string);
  if (!isNaN(d.getTime())) return d.toLocaleString();
  return String(value);
}

const AdminDashboard = (): JSX.Element => {
  const { apiClient, user } = useAuth();
  const { setSnack } = useAppState();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [recentUsers, setRecentUsers] = useState<UserSummary[]>([]);
  const [recentChats, setRecentChats] = useState<Types.ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<Types.ChatSession | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const resp = await apiClient.getAdminOverview();
      const data = resp?.data || resp;
      setOverview(data);
      setRecentUsers(data?.recentUserAccess || []);
      setRecentChats(data?.recentChats || []);
    } catch (error) {
      console.error(error);
      setSnack('Unable to load admin metrics', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !user.isAdmin) return;
    loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setAdminForUser = async (id: string, makeAdmin: boolean) => {
    try {
      await apiClient.setUserAdmin(id, makeAdmin);
      setSnack(`User ${makeAdmin ? 'granted' : 'revoked'} admin`, 'success');
      loadMetrics();
    } catch (error) {
      console.error(error);
      setSnack('Unable to update admin flag', 'error');
    }
  };

  if (!user || !user.isAdmin) {
    return (
      <Box p={3}>
        <Typography variant="h5">Admin Access Required</Typography>
        <Typography variant="body1">You must be an administrator to view this page.</Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h4" mb={2}>
        Admin Dashboard
      </Typography>
      {loading && <CircularProgress />}

      {overview && (
        <Grid container spacing={2} mb={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Users</Typography>
                <Typography variant="h4">{overview.totalUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6">New (24h)</Typography>
                <Typography variant="h4">{overview.newUsersLastDay}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6">Active (24h)</Typography>
                <Typography variant="h4">{overview.activeUsersLastDay}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box mb={3}>
        <Typography variant="h6">Recent Users</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentUsers.map(u => (
                <TableRow key={u.id || u.email}>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{formatDate(u.lastLogin)}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!u.id && !u.email}
                      onClick={() => {
                        const id = u.id ?? u.email;
                        if (!id) {
                          setSnack('Unable to determine user id/email', 'error');
                          return;
                        }
                        setAdminForUser(id, true);
                      }}
                    >
                      Make Admin
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Typography variant="h6">Recent Chat Sessions</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Session ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Last Activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentChats.map(c => (
                <TableRow
                  key={c.id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedSession(c)}
                >
                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.userId || c.guestId}</TableCell>
                  <TableCell>{c.title}</TableCell>
                  <TableCell>{formatDate(c.lastActivity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <AdminSessionModal
        open={Boolean(selectedSession)}
        onClose={() => setSelectedSession(null)}
        session={selectedSession}
      />
    </Box>
  );
};

export { AdminDashboard };
